import { addDays, startOfMonth, endOfMonth, startOfDay } from "date-fns";
import type { DateRange, DateRangeWithLabel } from "@/utils/dateUtils";
import { calcZonedDate, nowInTimezone } from "@/utils/dateUtils";
import { LuBan, LuInfo } from "react-icons/lu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TZDate } from "@date-fns/tz";

enum ChunkBoundaryType {
  NONE = "NONE",
  START = "START",
  END = "END",
  BOTH = "BOTH",
}

interface Props {
  date: Date;
  handleDateClick: (date: Date) => void;
  dateRange: DateRangeWithLabel | null;
  aggregate?: number | "";
  aggregateDateChunks?: DateRange[] | null;
  first_allowed_date?: Date;
  last_allowed_date?: Date;
  timezone: string;
  compareDate?: DateRangeWithLabel | null;
}

const CalendarMonth = ({
  date,
  handleDateClick,
  dateRange,
  aggregateDateChunks,
  first_allowed_date,
  last_allowed_date,
  timezone,
  compareDate,
}: Props) => {
  const startDate =
    aggregateDateChunks && aggregateDateChunks.length
      ? aggregateDateChunks[0]?.from
      : dateRange?.from;
  const endDate = dateRange?.to;
  const generateDates = (month: Date) => {
    const start = new TZDate(calcZonedDate(month, timezone, startOfMonth), timezone);
    const end = new TZDate(calcZonedDate(month, timezone, endOfMonth), timezone);

    const dates: (TZDate | null)[] = [];

    // Add null values for padding days before the start of the month
    const startDayOfWeek = start.getDay(); // 0 = Sunday, 1 = Monday, etc.
    for (let i = 0; i < startDayOfWeek; i++) {
      dates.push(null);
    }

    // Add the actual dates
    for (
      let d = start;
      d <= end;
      d = new TZDate(calcZonedDate(d, timezone, addDays, 1), timezone)
    ) {
      dates.push(d);
    }
    return dates;
  };

  const handleGridClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "BUTTON") {
      const dateStr = target.dataset.date;
      if (dateStr) {
        handleDateClick(new TZDate(dateStr, timezone));
      }
    }
  };

  const checkBoundaryType = (
    targetDate: Date,
    chunkStart: Date,
    chunkEnd: Date,
  ): ChunkBoundaryType => {
    const dateTime = calcZonedDate(targetDate, timezone, startOfDay).getTime();
    const chunkStartTime = calcZonedDate(chunkStart, timezone, startOfDay).getTime();
    const chunkEndTime = calcZonedDate(chunkEnd, timezone, startOfDay).getTime();
    if (dateTime === chunkStartTime) {
      return dateTime === chunkEndTime ? ChunkBoundaryType.BOTH : ChunkBoundaryType.START;
    }
    if (dateTime === chunkEndTime) {
      return ChunkBoundaryType.END;
    }
    return ChunkBoundaryType.NONE;
  };

  const getChunkBoundaryType = (targetDate: Date): ChunkBoundaryType => {
    if (!startDate || !endDate) return ChunkBoundaryType.NONE;

    if (aggregateDateChunks && aggregateDateChunks.length > 0) {
      for (const chunk of aggregateDateChunks) {
        const boundaryType = checkBoundaryType(targetDate, chunk.from, chunk.to);
        if (boundaryType !== ChunkBoundaryType.NONE) return boundaryType;
      }
    } else {
      return checkBoundaryType(targetDate, startDate, endDate);
    }

    return ChunkBoundaryType.NONE;
  };

  const isDateDisabled = (date: Date): boolean => {
    const nowInTZ = nowInTimezone(timezone);
    // Check if date is after today in account's timezone
    if (date > nowInTZ) {
      return true;
    }

    // Check if date is before first_allowed_date
    if (first_allowed_date && date < first_allowed_date) {
      return true;
    }

    // Check if date is after last_allowed_date
    if (last_allowed_date && date > last_allowed_date) {
      return true;
    }

    return false;
  };

  const isFirstAllowedMonth =
    first_allowed_date &&
    calcZonedDate(date, timezone, startOfMonth).getTime() ===
      calcZonedDate(first_allowed_date, timezone, startOfMonth).getTime();

  const compareStartDateTime = compareDate?.from
    ? calcZonedDate(compareDate.from, timezone, startOfDay).getTime()
    : null;
  const compareEndDateTime = compareDate?.to
    ? calcZonedDate(compareDate.to, timezone, startOfDay).getTime()
    : null;

  return (
    <div className="flex-1 flex flex-col gap-4 m-0 p-0">
      {isFirstAllowedMonth && (
        <div className="w-full flex justify-center mt-6">
          <div className="flex items-center gap-[6px] px-[14px] py-[6px] rounded-[8px] bg-[#18181B]">
            <LuBan className="w-[12px] h-[12px]" color="#F93C3C" />
            <span className="font-['Inter'] text-[#FAFAFA] text-[12px] font-normal leading-[20px]">
              Past dates unavailable for sync.
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <LuInfo className="w-[12px] h-[12px]" color="#FAFAFA4D" />
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="start"
                  sideOffset={12}
                  alignOffset={100}
                  className="rounded-[12px]"
                >
                  <p className="font-['Inter'] text-[#FAFAFA] text-[12px] font-normal leading-[20px]">
                    Contact the GetCrux team via the Pylon support channel to <br />
                    request an extension of your sync duration.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      )}
      <div className="pl-[32px] text-[#617390] text-[10.8px] font-medium leading-[14.4px] tracking-[0.216px]">
        {date.toLocaleString("en-US", {
          month: "short",
          year: "numeric",
          timeZone: timezone,
        })}
      </div>

      <div
        className="grid grid-cols-7 gap-y-[5.4px] pl-[11.45px] pr-[11.45px]"
        onClick={handleGridClick}
      >
        {generateDates(date).map((date, index) =>
          date ? (
            <div
              key={date.toString()}
              className={`flex justify-center items-center overflow-hidden
                ${getChunkBoundaryType(date) === ChunkBoundaryType.START ? "bg-gradient-to-r from-transparent to-[#FFF4EE]" : ""}
                ${getChunkBoundaryType(date) === ChunkBoundaryType.END ? "bg-gradient-to-l from-transparent to-[#FFF4EE]" : ""}
                ${startDate && endDate && calcZonedDate(startDate, timezone, startOfDay).getTime() < calcZonedDate(date, timezone, startOfDay).getTime() && calcZonedDate(endDate, timezone, startOfDay).getTime() > calcZonedDate(date, timezone, startOfDay).getTime() && getChunkBoundaryType(date) === ChunkBoundaryType.NONE ? "bg-[#FFF4EE]" : ""}
                ${compareEndDateTime !== compareStartDateTime && compareStartDateTime === calcZonedDate(date, timezone, startOfDay).getTime() ? "!bg-gradient-to-r from-transparent to-[#f1f2f6]" : ""}
                ${compareEndDateTime !== compareStartDateTime && compareEndDateTime === calcZonedDate(date, timezone, startOfDay).getTime() ? "!bg-gradient-to-l from-transparent to-[#f1f2f6]" : ""}
                ${compareStartDateTime && compareEndDateTime && compareStartDateTime < calcZonedDate(date, timezone, startOfDay).getTime() && compareEndDateTime > calcZonedDate(date, timezone, startOfDay).getTime() ? "bg-gradient-to-r from-[#f1f2f6] from-0% via-[#f1f2f6] via-50% to-[#f1f2f6] to-50% to-100%" : ""}
                ${getChunkBoundaryType(date) === ChunkBoundaryType.START && compareEndDateTime === calcZonedDate(date, timezone, startOfDay).getTime() ? "!bg-[linear-gradient(to_right,#f1f2f6_0,#f1f2f6_50%,#FFF4EE_50%,#FFF4EE_100%)]" : ""}
                ${getChunkBoundaryType(date) === ChunkBoundaryType.START && compareStartDateTime && compareEndDateTime && compareStartDateTime < calcZonedDate(date, timezone, startOfDay).getTime() && compareEndDateTime > calcZonedDate(date, timezone, startOfDay).getTime() && "!bg-[#f1f2f6]"}
                ${getChunkBoundaryType(date) === ChunkBoundaryType.END && compareStartDateTime && compareEndDateTime && compareStartDateTime < calcZonedDate(date, timezone, startOfDay).getTime() && compareEndDateTime > calcZonedDate(date, timezone, startOfDay).getTime() && "!bg-[#f1f2f6]"}

              `}
            >
              <button
                data-date={date.toString()}
                disabled={isDateDisabled(date)}
                className={`min-w-[28.8px] h-[28.8px] w-[28.8px] text-brand-primary-new text-[10.8px] font-normal disabled:text-[#61739099] disabled:cursor-not-allowed 
                ${
                  [ChunkBoundaryType.START, ChunkBoundaryType.BOTH].includes(
                    getChunkBoundaryType(date),
                  )
                    ? "rounded-l-full"
                    : ""
                }
                ${
                  [ChunkBoundaryType.END, ChunkBoundaryType.BOTH].includes(
                    getChunkBoundaryType(date),
                  )
                    ? "rounded-r-full"
                    : ""
                }
               ${
                 startDate &&
                 endDate &&
                 (calcZonedDate(startDate, timezone, startOfDay).getTime() ===
                   calcZonedDate(date, timezone, startOfDay).getTime() ||
                   calcZonedDate(endDate, timezone, startOfDay).getTime() ===
                     calcZonedDate(date, timezone, startOfDay).getTime())
                   ? `bg-[#ff4500]  text-white rounded-full`
                   : ""
               }
               ${
                 compareDate?.from &&
                 compareDate?.to &&
                 (calcZonedDate(compareDate.from, timezone, startOfDay).getTime() ===
                   calcZonedDate(date, timezone, startOfDay).getTime() ||
                   calcZonedDate(compareDate.to, timezone, startOfDay).getTime() ===
                     calcZonedDate(date, timezone, startOfDay).getTime())
                   ? "!bg-[#67749B] rounded-full text-white"
                   : ""
               }
          
                `}
              >
                {date.getDate()}
              </button>
            </div>
          ) : (
            <div key={`empty-${index}`} className="invisible" />
          ),
        )}
      </div>
    </div>
  );
};

export default CalendarMonth;
